import { FieldType, FormSplitConfiguration } from '#types/System.types';
import type { ArRowData } from '#types/System.Artifacts.types';
import { getSplitQueryKeyAndLabel } from '@/services/getSplitQueryKeyAndLabel';
import { resolveSplitLabel } from '@/services/resolveSplitLabel';

/**
 * Represents different modes for form splitting
 */
export type FormSplitMode = 'no-split' | 'show-value';

/**
 * Represents an option in the split control dropdown
 */
export interface FormSplitOption {
    /** Unique value for this option */
    value: string;
    /** Display label for the option */
    label: string;
    /** The mode this option represents */
    mode: FormSplitMode;
    /** The actual value being filtered for (for show-value mode) */
    actualValue?: string;
    /** Reference to the split configuration */
    splitRef?: import('#types/System.types').SplitByReference;
    /** Number of records matching this option */
    recordCount?: number;
}

/**
 * Generates split options for the form splitting control.
 * 
 * Form fields are NOT an input — unique values come only from `rowsByTableId`,
 * the AR-table rows `useArTableRowsForSplit` binds from Firestore. The embedded
 * `TableValue` (`field.value.rows`) shape this used to also scan was superseded
 * by ArTable and is gone; do not re-add a `fields` parameter.
 *
 * - Always includes a "No split" option for standard form view
 * - Creates one "show-value" option per unique value of each split criterion
 * - Counts rows per option to provide context
 *
 * @param splitConfig - Configuration defining how splitting should work
 * @param fieldTypes - Field types used to resolve each criterion's label
 * @param rowsByTableId - AR-table rows keyed by table field id
 * @returns Array of available split options with counts and metadata
 */
export const generateSplitOptions = (
    splitConfig: FormSplitConfiguration | undefined,
    fieldTypes: Record<string, FieldType>,
    rowsByTableId?: Record<string, readonly ArRowData[]>
): FormSplitOption[] => {
    const options: FormSplitOption[] = [];

    // Always add "No split" option
    options.push({
        value: 'no-split',
        label: 'No split',
        mode: 'no-split',
        recordCount: 0
    });

    if (!splitConfig?.enabled || !splitConfig.splitBy) {
        return options;
    }

    splitConfig.splitBy.forEach((splitRef, index) => {
        const criterionLabel = resolveSplitLabel(splitRef, fieldTypes);
        const uniqueValues = new Map<string, { count: number; label: string }>();

        if (rowsByTableId?.[splitRef.fieldId]) {
            const rows = rowsByTableId[splitRef.fieldId] ?? [];
            rows.forEach((row) => {
                const cellValue = row.cells?.[splitRef.valuePath]?.value;
                const { key, label } = getSplitQueryKeyAndLabel(cellValue);

                if (!key) return;
                const prev = uniqueValues.get(key);
                uniqueValues.set(key, { count: (prev?.count ?? 0) + 1, label: prev?.label ?? label });
            });
        }

        // Add individual "Show X" options for each unique value
        uniqueValues.forEach((entry, key) => {
            options.push({
                value: `show-${index}:${key}`,
                label: `${criterionLabel}: ${entry.label || key}`,
                mode: 'show-value',
                actualValue: key,
                splitRef: splitRef,
                recordCount: entry.count
            });
        });
    });

    return options;
};